import React from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import {
  DetailDownloadControls,
  DetailRowsSection,
  DetailSearch,
  renderBackIcon,
} from "../../../shared/ui/detail-page/detail-page";
import * as DetailStyled from "../../../shared/ui/detail-page/detail-page.styled";
import { SectionSidebarNav } from "../../../shared/ui/section-sidebar-nav/section-sidebar-nav";
import { getOrderProcedureById } from "../model/orders-procedures";

import * as Styled from "./orders-procedures-detail.styled";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OrdersProceduresDetail = () => {
  const navigate = useNavigate();
  const { orderID } = useParams();
  const order = getOrderProcedureById(orderID);
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const contentScrollerRef = React.useRef<HTMLDivElement | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    if (!setIsStretchContent) {
      return;
    }

    setIsStretchContent(true);

    return () => {
      setIsStretchContent(false);
    };
  }, [setIsStretchContent]);

  const navigationSections = React.useMemo(
    () => [
      { id: "order-detail", label: "Order Detail" },
      ...(order.resultDetails
        ? [{ id: "result-detail", label: "Result Detail" }]
        : []),
      ...(order.resultNotes
        ? [{ id: "result-notes", label: "Result Notes" }]
        : []),
    ],
    [order.resultDetails, order.resultNotes],
  );

  return (
    <Styled.Container>
      <DetailStyled.BackBtn type="button" onClick={() => navigate(-1)}>
        {renderBackIcon()}
        Back
      </DetailStyled.BackBtn>
      <DetailStyled.PageHeader>
        <Styled.Title>Order Details</Styled.Title>
        <DetailStyled.HeaderActions>
          <DetailDownloadControls fileName="order-details" />
          <DetailSearch value={searchQuery} onChange={setSearchQuery} />
        </DetailStyled.HeaderActions>
      </DetailStyled.PageHeader>
      <DetailStyled.DetailLayout>
        <SectionSidebarNav
          ariaLabel="Order detail sections"
          sections={navigationSections}
          scrollContainerRef={contentScrollerRef}
        />
        <Styled.ContentScroller ref={contentScrollerRef}>
          <DetailStyled.EntityContainer>
            <DetailRowsSection id="order-detail" title="Order Detail">
              <DetailStyled.DetailRows>
                {order.orderDetails.map((row) => (
                  <DetailStyled.DetailRow key={row.label}>
                    <DetailStyled.Description>
                      {row.label}
                    </DetailStyled.Description>
                    <DetailStyled.Value>{row.value}</DetailStyled.Value>
                  </DetailStyled.DetailRow>
                ))}
              </DetailStyled.DetailRows>
            </DetailRowsSection>
            {order.resultDetails ? (
              <DetailRowsSection id="result-detail" title="Result Detail">
                <DetailStyled.DetailRows>
                  {order.resultDetails.map((row) => (
                    <DetailStyled.DetailRow key={row.label}>
                      <DetailStyled.Description>
                        {row.label}
                      </DetailStyled.Description>
                      <DetailStyled.Value>{row.value}</DetailStyled.Value>
                    </DetailStyled.DetailRow>
                  ))}
                </DetailStyled.DetailRows>
              </DetailRowsSection>
            ) : null}
            {order.resultNotes ? (
              <DetailRowsSection id="result-notes" title="Result Notes">
                <DetailStyled.Notes>{order.resultNotes}</DetailStyled.Notes>
              </DetailRowsSection>
            ) : null}
          </DetailStyled.EntityContainer>
        </Styled.ContentScroller>
      </DetailStyled.DetailLayout>
    </Styled.Container>
  );
};
